import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiPopper } from "@miplatform/components";
import { useRouter } from "@miplatform/hooks";
import {
  IAccountResponse,
  IBidResponse,
  ICommunitiesResponse,
  IDepartmentResponse,
  ISeriesGroupResponse,
  ISeriesResponse,
} from "@miplatform/types";
import { MenuItem, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { StatusChip } from "../../components/StatusChip/StatusChip";
import { BidPopperContent } from "./BidPopperContent";

export function BidSearchOption({
  option,
  props,
  close,
  accounts,
  communities,
  seriesGroups,
  series,
  departments,
}: Readonly<BidSearchOptionProps>) {
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { navigate } = useRouter();
  const { classes, theme } = useStyles();

  const openPopper = (event: React.MouseEvent<HTMLElement>) => {
    const { currentTarget } = event;
    const timeout = setTimeout(() => {
      if (!anchorEl) {
        setAnchorEl(currentTarget);
      }
    }, 250);
    setTimeoutId(timeout);
  };

  const closePopper = () => {
    setAnchorEl(null);
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  };

  const handleOnClick = (event: React.MouseEvent<HTMLLIElement>) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`/estimating/bids/create/${option.id}`);
    closePopper();
    close();
  };

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const communityName = communities.find(
    (item) => item.id === option.communityId
  )?.name;

  const builderName = accounts.find(
    (item) => item.id === option.builderId
  )?.name;

  const seriesGroupName = seriesGroups.find(
    (item) => item.id === option.seriesGroupId
  )?.name;

  const seriesName = series.find((item) => item.id === option.seriesId)?.name;

  const assigneeName = departments
    .flatMap((department) => department.users)
    .find((user) => user.id === option.assigneeId)?.firstName;

  return (
    <MenuItem
      {...props}
      onClick={handleOnClick}
      onMouseEnter={openPopper}
      onMouseLeave={closePopper}
    >
      <Box className={classes.optionRow}>
        <Typography>
          {option.name} - {option.builderName}
        </Typography>
        <StatusChip status={option.planStatus} />
      </Box>
      <MiPopper
        anchorEl={anchorEl}
        showArrow
        arrowColor={theme.palette.primary.main}
        open={Boolean(anchorEl)}
        placement="right"
        className={classes.popper}
      >
        <BidPopperContent
          option={option}
          communityName={communityName}
          builderName={builderName}
          seriesGroupName={seriesGroupName}
          seriesName={seriesName}
          assigneeName={assigneeName}
        />
      </MiPopper>
    </MenuItem>
  );
}

const useStyles = makeStyles()((theme) => ({
  optionRow: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  popper: {
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 4,
  },
}));

interface BidSearchOptionProps {
  option: IBidResponse;
  props: any;
  close: () => void;
  accounts: IAccountResponse[];
  communities: ICommunitiesResponse[];
  seriesGroups: ISeriesGroupResponse[];
  series: ISeriesResponse[];
  departments: IDepartmentResponse[];
}
